import { FC } from "react";

import {
  CampaignPaymentTab,
  FormType,
  LIVE_EDITOR_STEP_KEYS,
  LiveEditorStepKey,
  OrganizationAssistantDashboardPageParams,
  OrganizationAssistantDestination,
} from "@simplyk/common";

import { DESTINATIONS } from "../helpers/getOrganizationToolPartDisplay";
import { isRecord } from "../helpers/isRecord";

import { buildDestinationRoute } from "./helpers/buildDestinationRoute";
import { useDestinationLabels } from "./helpers/useDestinationLabels";
import { useNavigationAction } from "./helpers/useNavigationAction";
import { NavigationButton } from "./NavigationButton";
import { StripeOnboardingButton } from "./StripeOnboardingButton/StripeOnboardingButton";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useUrlBuilder } from "@/common/hooks/useUrlBuilder";
import type { AssistantToolRendererProps } from "@/features/Assistant/contexts/AssistantConfigContext";

export const ConnectedNavigationButton: FC<AssistantToolRendererProps> = ({ part }) => {
  const page = readDashboardPage(part.output);
  const { isoLocale } = useLocaleContext();
  const buildUrl = useUrlBuilder();
  const labels = useDestinationLabels();
  const { openRoute, verifyIdentity, openStripeOnboarding, notice, isVerifyingIdentity } = useNavigationAction(
    page?.destination ?? null
  );

  if (!page) {
    return null;
  }
  const target = buildDestinationRoute(page.destination, page.params, { isoLocale, currentPath: buildUrl({}) });
  const label = labels[page.destination];
  if (!target) {
    return null;
  }
  if (target.kind === "stripe_onboarding") {
    return <StripeOnboardingButton label={label} onOpen={openStripeOnboarding} />;
  }
  if (target.kind === "identity_verification") {
    return <NavigationButton label={label} isLoading={isVerifyingIdentity} notice={notice} onClick={verifyIdentity} />;
  }
  return <NavigationButton label={label} onClick={() => openRoute(target)} />;
};

const FORM_TYPES: ReadonlySet<string> = new Set(Object.values(FormType));
const HUB_TABS: ReadonlySet<string> = new Set(Object.values(CampaignPaymentTab));
const EDITOR_STEPS: ReadonlySet<string> = new Set(LIVE_EDITOR_STEP_KEYS);

const readString = (value: unknown): string | undefined => (typeof value === "string" && value ? value : undefined);

const isDestination = (value: unknown): value is OrganizationAssistantDestination =>
  typeof value === "string" && DESTINATIONS.has(value);

const isFormType = (value: unknown): value is FormType => typeof value === "string" && FORM_TYPES.has(value);

const isHubTab = (value: unknown): value is CampaignPaymentTab => typeof value === "string" && HUB_TABS.has(value);

const isEditorStep = (value: unknown): value is LiveEditorStepKey =>
  typeof value === "string" && EDITOR_STEPS.has(value);

const readDashboardPage = (
  output: unknown
): { destination: OrganizationAssistantDestination; params: OrganizationAssistantDashboardPageParams } | null => {
  if (!isRecord(output) || "error" in output || !isDestination(output.destination)) {
    return null;
  }
  const params = isRecord(output.params) ? output.params : {};
  return {
    destination: output.destination,
    params: {
      formId: readString(params.formId),
      formType: isFormType(params.formType) ? params.formType : undefined,
      transactionId: readString(params.transactionId),
      payoutId: readString(params.payoutId),
      step: isEditorStep(params.step) ? params.step : undefined,
      tab: isHubTab(params.tab) ? params.tab : undefined,
    },
  };
};
